import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  ListChecks,
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Power,
  TrendingUp,
  Clock,
  Target,
  CheckCircle2,
  MoreHorizontal,
  Zap,
  Users,
  Brain,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { generateMockStrategies } from '@/lib/mock-data/strategies-mock-data';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

const StrategiesPage: React.FC = () => {
  const [strategies] = useState<CollectionStrategyItem[]>(generateMockStrategies());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedStrategy, setSelectedStrategy] = useState<CollectionStrategyItem | null>(null);
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Form state for new/edit strategy
  const [formData, setFormData] = useState({
    name: '',
    type: 'Standard' as CollectionStrategyItem['type'],
    description: '',
    status: 'Active' as 'Active' | 'Inactive',
    triggerType: 'days_overdue' as any,
    daysOverdue: 30,
    amountThreshold: 5000,
    actions: [] as any[],
    assignmentRules: [] as any[],
    aiEnabled: false,
    aiTone: 3,
    aiFormality: 'professional' as 'casual' | 'professional' | 'formal',
    aiPersonalization: 'moderate' as 'minimal' | 'moderate' | 'deep',
    aiContextFactors: {
      paymentHistory: true,
      riskProfile: true,
      relationshipDuration: false,
      industryContext: false,
      economicFactors: false,
      previousDisputes: false,
      communicationPreferences: false,
    },
    aiTimingOptimization: true,
  });

  // Filter strategies
  const filteredStrategies = useMemo(() => {
    let filtered = strategies;

    if (filterType !== 'all') {
      filtered = filtered.filter(s => s.type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [strategies, filterType, filterStatus, searchQuery]);

  const handleCreateStrategy = () => {
    setSelectedStrategy(null);
    setActiveTab('overview');
    setFormData({
      name: '',
      type: 'Standard',
      description: '',
      status: 'Active',
      triggerType: 'days_overdue',
      daysOverdue: 30,
      amountThreshold: 5000,
      actions: [],
      assignmentRules: [],
      aiEnabled: false,
      aiTone: 3,
      aiFormality: 'professional',
      aiPersonalization: 'moderate',
      aiContextFactors: {
        paymentHistory: true,
        riskProfile: true,
        relationshipDuration: false,
        industryContext: false,
        economicFactors: false,
        previousDisputes: false,
        communicationPreferences: false,
      },
      aiTimingOptimization: true,
    });
    setStrategyModalOpen(true);
  };

  const handleEditStrategy = (strategy: CollectionStrategyItem) => {
    setSelectedStrategy(strategy);
    setActiveTab('overview');
    setFormData({
      name: strategy.name,
      type: strategy.type,
      description: strategy.description,
      status: strategy.status,
      triggerType: strategy.trigger.type,
      daysOverdue: strategy.trigger.daysOverdue || 30,
      amountThreshold: strategy.trigger.amountThreshold || 5000,
      actions: strategy.actions,
      assignmentRules: strategy.assignmentRules,
      aiEnabled: !!strategy.aiBehavior?.enabled,
      aiTone: strategy.aiBehavior?.communicationTone || 3,
      aiFormality: strategy.aiBehavior?.formalityLevel || 'professional',
      aiPersonalization: strategy.aiBehavior?.personalizationDepth || 'moderate',
      aiContextFactors: strategy.aiBehavior?.contextFactors || {
        paymentHistory: true,
        riskProfile: true,
        relationshipDuration: false,
        industryContext: false,
        economicFactors: false,
        previousDisputes: false,
        communicationPreferences: false,
      },
      aiTimingOptimization: strategy.aiBehavior?.timingOptimization || true,
    });
    setStrategyModalOpen(true);
  };

  const handleToggleStatus = (strategy: CollectionStrategyItem) => {
    const newStatus = strategy.status === 'Active' ? 'Inactive' : 'Active';
    toast.success(`Strategy ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
  };

  const handleSaveStrategy = () => {
    // Basic validation
    if (!formData.name.trim()) {
      toast.error('Please enter a strategy name');
      setActiveTab('overview');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a strategy description');
      setActiveTab('overview');
      return;
    }
    
    toast.success(selectedStrategy ? 'Strategy updated successfully' : 'Strategy created successfully');
    setStrategyModalOpen(false);
  };

  const getTypeBadge = (type: CollectionStrategyItem['type']) => {
    const colors: Record<CollectionStrategyItem['type'], string> = {
      Standard: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      Intensive: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      VIP: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'High Risk': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return <Badge className={colors[type]}>{type}</Badge>;
  };

  const getPerformanceBadge = (successRate: number) => {
    if (successRate >= 80) return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    if (successRate >= 60) return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
    if (successRate >= 40) return <Badge className="bg-amber-100 text-amber-800">Average</Badge>;
    return <Badge className="bg-red-100 text-red-800">Poor</Badge>;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ListChecks className="h-8 w-8" />
            Collection Strategies
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure automated collection workflows and assignment rules
          </p>
        </div>
        <Button onClick={handleCreateStrategy}>
          <Plus className="h-4 w-4 mr-2" />
          Create Strategy
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Standard">Standard</SelectItem>
            <SelectItem value="Intensive">Intensive</SelectItem>
            <SelectItem value="VIP">VIP</SelectItem>
            <SelectItem value="High Risk">High Risk</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Strategies Table */}
      <Card>
        <CardHeader>
          <CardTitle>Strategies ({filteredStrategies.length})</CardTitle>
          <CardDescription>
            Manage your collection strategies and monitor their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name & Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Active Cases</TableHead>
                <TableHead>Success Rate</TableHead>
                <TableHead>Avg Days</TableHead>
                <TableHead>Performance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStrategies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No strategies found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredStrategies.map((strategy) => (
                  <TableRow key={strategy.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{strategy.name}</div>
                        <div className="text-sm text-muted-foreground max-w-md truncate">
                          {strategy.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(strategy.type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={strategy.status === 'Active'}
                          onCheckedChange={() => handleToggleStatus(strategy)}
                        />
                        <span className="text-sm">
                          {strategy.status === 'Active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        {strategy.activeCasesCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        {strategy.successRate}%
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {strategy.avgDaysToPayment} days
                      </div>
                    </TableCell>
                    <TableCell>{getPerformanceBadge(strategy.successRate)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditStrategy(strategy)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Strategy
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Eye className="h-4 w-4 mr-2" />
                            View Performance
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(strategy)}>
                            <Power className="h-4 w-4 mr-2" />
                            {strategy.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Strategy Modal */}
      <Dialog open={strategyModalOpen} onOpenChange={setStrategyModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedStrategy ? `Edit Strategy: ${selectedStrategy.name}` : 'Create New Strategy'}
            </DialogTitle>
            <DialogDescription>
              Configure your collection strategy settings across all sections
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="triggers" className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Triggers
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Actions
              </TabsTrigger>
              <TabsTrigger value="assignment" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Assignment
              </TabsTrigger>
              <TabsTrigger value="ai-behavior" className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                AI Behavior
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-4">
              {/* Tab 1: Overview */}
              <TabsContent value="overview" className="space-y-4 mt-0">
              <div className="space-y-4">
                <div>
                  <Label>Strategy Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Standard 30-Day Collection"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Intensive">Intensive</SelectItem>
                      <SelectItem value="VIP">VIP</SelectItem>
                      <SelectItem value="High Risk">High Risk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe when and how this strategy should be used..."
                    rows={4}
                  />
                </div>
              </div>
              </TabsContent>

              {/* Tab 2: Triggers */}
              <TabsContent value="triggers" className="space-y-4 mt-0">
                <div className="space-y-4">
                  <div>
                    <Label>Trigger Type</Label>
                    <Select
                      value={formData.triggerType}
                      onValueChange={(value: any) => setFormData({ ...formData, triggerType: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days_overdue">Days Overdue</SelectItem>
                        <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                        <SelectItem value="risk_score">Risk Score</SelectItem>
                        <SelectItem value="manual_only">Manual Only</SelectItem>
                        <SelectItem value="payment_pattern_change">Payment Pattern Change</SelectItem>
                        <SelectItem value="promise_broken">Promise Broken</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.triggerType === 'days_overdue' && (
                    <div>
                      <Label>Days Overdue Threshold</Label>
                      <Input
                        type="number"
                        value={formData.daysOverdue}
                        onChange={(e) => setFormData({ ...formData, daysOverdue: parseInt(e.target.value) || 0 })}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Trigger when invoice is overdue by this many days
                      </p>
                    </div>
                  )}

                  {formData.triggerType === 'amount_threshold' && (
                    <div>
                      <Label>Amount Threshold ($)</Label>
                      <Input
                        type="number"
                        value={formData.amountThreshold}
                        onChange={(e) => setFormData({ ...formData, amountThreshold: parseInt(e.target.value) || 0 })}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Trigger when outstanding amount exceeds this value
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Tab 3: Actions */}
              <TabsContent value="actions" className="space-y-4 mt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Collection Actions</Label>
                    <Button size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Action
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">Send Email Reminder</div>
                        <div className="text-sm text-muted-foreground">Day 0 from trigger</div>
                      </div>
                      <Button size="sm" variant="ghost">Edit</Button>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">Phone Call</div>
                        <div className="text-sm text-muted-foreground">Day 7 from trigger</div>
                      </div>
                      <Button size="sm" variant="ghost">Edit</Button>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">Send Payment Link</div>
                        <div className="text-sm text-muted-foreground">Day 14 from trigger</div>
                      </div>
                      <Button size="sm" variant="ghost">Edit</Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Tab 4: Assignment */}
              <TabsContent value="assignment" className="space-y-4 mt-0">
                <div className="space-y-4">
                  <Label>Assignment Criteria</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Checkbox id="customer-segment" />
                        <Label htmlFor="customer-segment" className="cursor-pointer">
                          Customer Segment
                        </Label>
                      </div>
                      <Select>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select segment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                          <SelectItem value="mid-market">Mid-Market</SelectItem>
                          <SelectItem value="smb">SMB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Checkbox id="risk-profile" />
                        <Label htmlFor="risk-profile" className="cursor-pointer">
                          Risk Profile
                        </Label>
                      </div>
                      <Select>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select risk" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High Risk</SelectItem>
                          <SelectItem value="medium">Medium Risk</SelectItem>
                          <SelectItem value="low">Low Risk</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Checkbox id="amount-range" />
                        <Label htmlFor="amount-range" className="cursor-pointer">
                          Amount Range
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Input type="number" placeholder="Min" className="w-24" />
                        <Input type="number" placeholder="Max" className="w-24" />
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Tab 5: AI Behavior */}
              <TabsContent value="ai-behavior" className="space-y-4 mt-0">
                <div className="space-y-6">
                  <div>
                    <Label>Communication Tone</Label>
                    <div className="space-y-2 mt-2">
                      <Slider
                        value={[formData.aiTone]}
                        onValueChange={(value) => setFormData({ ...formData, aiTone: value[0] })}
                        min={1}
                        max={5}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Highly Empathetic</span>
                        <span>Balanced</span>
                        <span>Firm Results</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Formality Level</Label>
                    <Select
                      value={formData.aiFormality}
                      onValueChange={(value: any) => setFormData({ ...formData, aiFormality: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Context Factors</Label>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox id="payment-history" defaultChecked />
                        <Label htmlFor="payment-history" className="cursor-pointer">
                          Payment History
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="risk-profile-ai" defaultChecked />
                        <Label htmlFor="risk-profile-ai" className="cursor-pointer">
                          Risk Profile
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="relationship-length" />
                        <Label htmlFor="relationship-length" className="cursor-pointer">
                          Relationship Length
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="previous-disputes" />
                        <Label htmlFor="previous-disputes" className="cursor-pointer">
                          Previous Disputes
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setStrategyModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveStrategy}>
              {selectedStrategy ? 'Update Strategy' : 'Create Strategy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategiesPage;